import { useState } from "react";
import { motion } from "framer-motion";
import SectionLabel from "../ui/SectionLabel";
import SplitHeading from "../ui/SplitHeading";
import { useLang } from "../../context/LangContext";
import { c } from "../../content";
import SectionSubtitle from "../ui/SectionSubtitle";

const TESTIMONIALS = [
  {
    quote: "Miłosz podszedł do projektu jak do własnego produktu. Zanim padła pierwsza linijka kodu, wiedział więcej o marce Balans niż my sami. Efekt przeszedł oczekiwania — strona buduje dokładnie tę atmosferę, której szukaliśmy.",
    author: "Zespół Balans",
    company: "Szkolenia Balans",
    project: "szkoleniabalans.pl",
    initials: "ZB",
    accent: "#C48870",
    accentRgb: "196,136,112",
    roleIdx: 0,
  },
  {
    quote: "Współpraca wzorowa — jasny harmonogram, zero niespodzianek, gotowość do wyjaśnień na każdym etapie. Portal wygląda premium i działa szybko. Klienci regularnie pytają nas, kto to robił.",
    author: "Małgorzata Aptacy",
    company: "Aptacy Nieruchomości",
    project: "aptacy-nieruchomosci.pl",
    initials: "MA",
    accent: "#8A9EB8",
    accentRgb: "138,158,184",
    roleIdx: 1,
  },
  {
    quote: "Aplikacja działała bez zarzutu — nawet offline w górach. Miłosz rozumiał od razu, że to narzędzie dla konkretnych ludzi w konkretnym miejscu i czasie. Dokładnie to dostaliśmy.",
    author: "Aleksander Walny",
    company: "Prywatna aplikacja wyprawowa",
    project: "Aplikacja wyprawowa",
    initials: "AW",
    accent: "#90A888",
    accentRgb: "144,168,136",
    roleIdx: 2,
  },
];

function TestimonialCard({ t, index, roles }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.article
      className={`reveal d${index + 1} flex flex-col relative overflow-hidden`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      animate={{ y: hovered ? -6 : 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      style={{
        padding: "2.5rem", borderRadius: 4,
        border: hovered ? `1px solid rgba(${t.accentRgb},0.30)` : "1px solid rgba(255,255,255,0.07)",
        background: hovered ? `rgba(${t.accentRgb},0.03)` : "rgba(255,255,255,0.025)",
        transition: "border-color 350ms ease, background 350ms ease",
      }}
    >
      <div aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: hovered ? `linear-gradient(to right, transparent, rgba(${t.accentRgb},0.5), transparent)` : "transparent", transition: "background 400ms ease" }} />
      <span aria-hidden="true" className="font-display absolute -top-3 left-5 pointer-events-none select-none" style={{ fontSize: "7rem", lineHeight: 1, color: t.accent, opacity: hovered ? 0.13 : 0.07, transition: "opacity 400ms ease" }}>&ldquo;</span>
      <p className="font-body text-sm leading-[1.95] relative z-10 flex-1" style={{ color: "rgba(242,232,216,0.70)" }}>{t.quote}</p>
      <div className="my-6 h-px" style={{ background: hovered ? `rgba(${t.accentRgb},0.18)` : "rgba(255,255,255,0.06)", transition: "background 350ms ease" }} />
      <div className="flex items-center gap-4">
        <div className="shrink-0 flex items-center justify-center font-mono text-[11px] tracking-[0.08em]" style={{ width: 44, height: 44, borderRadius: "50%", background: `rgba(${t.accentRgb},0.10)`, border: `1px solid rgba(${t.accentRgb},0.25)`, color: t.accent }}>
          {t.initials}
        </div>
        <div>
          <p className="font-body text-sm" style={{ color: "#F2E8D8", fontWeight: 500 }}>{t.author}</p>
          <p className="font-mono text-[9.5px] tracking-[0.18em] uppercase mt-0.5" style={{ color: "rgba(242,232,216,0.35)" }}>
            {roles[t.roleIdx]} · {t.company}
          </p>
        </div>
      </div>
      <div className="mt-5">
        <span className="font-mono text-[9px] tracking-[0.22em] uppercase px-2.5 py-1" style={{ borderRadius: 3, border: `1px solid rgba(${t.accentRgb},0.18)`, background: `rgba(${t.accentRgb},0.06)`, color: t.accent }}>
          {t.project}
        </span>
      </div>
    </motion.article>
  );
}

export default function Testimonials() {
  const { lang } = useLang();
  const roles = c.testimonials.roles[lang];
  const featured = c.testimonials.featured;
  const testimonials = TESTIMONIALS.map((t, i) => ({
    ...t,
    quote: c.testimonials.quotes[i][lang],
  }));

  const featuredText = featured.quote[lang];
  const highlight    = featured.highlight[lang];
  const splitIdx     = featuredText.lastIndexOf(highlight);
  const before       = splitIdx >= 0 ? featuredText.slice(0, splitIdx) : featuredText;
  const after        = splitIdx >= 0 ? highlight : "";

  return (
    <section id="opinie" className="section-pad relative overflow-hidden section-depth" style={{ background: "#0D0C0A" }} data-testid="testimonials-section">
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[300px]" style={{ background: "radial-gradient(ellipse, rgba(200,121,65,0.04), transparent 70%)", filter: "blur(60px)" }} />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-20 -right-20 w-[400px] h-[400px] rounded-full" style={{ background: "radial-gradient(circle, rgba(124,196,216,0.025), transparent 65%)", filter: "blur(80px)" }} />

      <div className="max-w-7xl mx-auto relative">
        <div className="reveal mb-16 lg:mb-20 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <SectionLabel>{c.testimonials.label[lang]}</SectionLabel>
            <SplitHeading
              line1={c.testimonials.heading1[lang]}
              line2={<em className="italic text-ice">{c.testimonials.ice[lang]}</em>}
              className="font-display font-light leading-[1.04] mt-5 text-white-warm"
              style={{ fontSize: "clamp(2.4rem, 4.8vw, 4rem)" }}
              data-testid="testimonials-title"
            />
          </div>
          <SectionSubtitle className="text-white-dim max-w-xs">
            {c.testimonials.subtitle[lang]}
          </SectionSubtitle>
        </div>

        <div className="reveal mb-14" style={{ borderLeft: "2px solid rgba(200,121,65,0.35)", paddingLeft: "2rem" }}>
          <p className="font-display font-light italic" style={{ fontSize: "clamp(1.35rem, 2.8vw, 2.2rem)", color: "rgba(242,232,216,0.72)", lineHeight: 1.45, maxWidth: "60ch" }}>
            "{before}<em style={{ color: "#C87941" }}>{after}</em>"
          </p>
          <p className="font-mono text-[10px] tracking-[0.26em] uppercase mt-5" style={{ color: "rgba(242,232,216,0.32)" }}>
            {featured.author} &nbsp;·&nbsp; {featured.company}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <TestimonialCard key={i} t={t} index={i} roles={roles} />
          ))}
        </div>

        <p className="reveal font-body text-xs text-center mt-14" style={{ color: "rgba(242,232,216,0.22)" }}>
          {c.testimonials.bottom_note[lang]}
        </p>
      </div>
    </section>
  );
}
